import React, { type ReactNode } from 'react';
import { FieldTypeIcon } from '@jira/project-settings__all-icons/src/allIcons.tsx';
import { Text } from '@atlaskit/primitives/compiled';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { IntlShape } from '@atlassian/jira-intl';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { getSecondaryLabelWithIcon } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/get-secondary-label-with-icon.tsx';
import { EPIC_LINK } from '../../common/constants.tsx';
import type { FieldToRender } from '../../common/types.tsx';
import type { IrremovableFields } from '../irremovable-fields/index.tsx';
import messages from '../messages.tsx';

type GetSelectedFields = {
	fieldsToRender: FieldToRender[];
	comparator: (x: string, y: string) => number;
	intl: IntlShape;
	irremovableFields: IrremovableFields;
};

export const getSelectedFields = ({
	fieldsToRender,
	comparator,
	intl,
	irremovableFields,
}: GetSelectedFields): FieldOption[] =>
	fieldsToRender
		.filter(
			({ fieldId }) =>
				// should not be an irremovable field
				!irremovableFields.has(fieldId),
		)
		.sort((a, b) => comparator(a.fieldLabel || '', b.fieldLabel || ''))
		.map(
			({
				fieldId,
				fieldLabel,
				fieldDescription,
				picker,
				fieldTypeDisplayName,
				shouldShowInContext,
			}) => {
				const notInContextFieldMessageIssueTermRefreshGalaxia = expVal(
					'project-terminology-refresh',
					'isEnabled',
					false,
				)
					? messages.notInContextFieldMessageIssueTermRefreshGalaxia
					: messages.notInContextFieldMessageIssueTermRefresh;
				const isDisabled = picker.searchTemplate === EPIC_LINK.searchTemplate;

				let isOnlyUnselectable = false;

				let disabledMessage = isDisabled
					? intl.formatMessage(messages.deprecatedEpicMessage, {
							strong: (chunks: ReactNode[]) => (
								<Text as="strong" color="inherit" size="UNSAFE_small">
									{chunks}
								</Text>
							),
						})
					: undefined;

				if (shouldShowInContext === false) {
					isOnlyUnselectable = true;
					disabledMessage = intl.formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? notInContextFieldMessageIssueTermRefreshGalaxia
							: messages.notInContextFieldMessage,
					);
				}

				if (isDisabled) {
					isOnlyUnselectable = true;
					disabledMessage = intl.formatMessage(messages.deprecatedEpicMessage, {
						strong: (chunks: ReactNode[]) => (
							<Text as="strong" color="inherit" size="UNSAFE_small">
								{chunks}
							</Text>
						),
					});
				}

				const icon = picker.fieldType ? (
					<FieldTypeIcon type={picker.fieldType} size="small" />
				) : undefined;

				const fieldOption: FieldOption = {
					optionType: 'field',
					label: fieldLabel || '',
					value: fieldId,
					isOnlyUnselectable,
					disabledMessage,
					searchTemplate: picker.searchTemplate,
					fieldType: picker.fieldType,
					secondaryLabel:
						fieldTypeDisplayName && icon
							? getSecondaryLabelWithIcon(icon, fieldTypeDisplayName)
							: undefined,
					fieldTypeDisplayName,
					fieldDescription,
					operators: picker.allowedOperators?.map((operator) => operator.value),
				};

				return fieldOption;
			},
		);
